import { useRef } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { EVENT_Z_INDICES } from '../../../constants';
import { useScrollDeepLinkedEventIntoView } from '../../../hooks/use-scroll-deep-linked-event-into-view';
import { useCalendarGridStore } from '../../../store/calendar-grid.store';
import { selectedEventStyle } from '../../styles';
import { OVERLAP_EVENTS_BUTTON_WIDTH } from './utils';

interface Props {
  style?: React.CSSProperties;
  hiddenEventIds: string[];
}

export const OverflowCountButton = ({ style, hiddenEventIds }: Props) => {
  const elementRef = useRef<HTMLButtonElement | null>(null);
  const { t } = useTranslation('calendar-grid');
  const onEventsSelect = useCalendarGridStore((s) => s.onEventsSelect);
  const isOverflowingEventSelected = useCalendarGridStore(
    (s) => !!s.selectedEventIds?.length && hiddenEventIds.some((id) => s.selectedEventIds?.includes(id))
  );
  // Highlight the overflow button when the deep-linked event is hidden behind it
  const isDeepLinkedHiddenEvent = useCalendarGridStore(
    (s) => !!s.deepLinkedEventId && hiddenEventIds.includes(s.deepLinkedEventId)
  );

  useScrollDeepLinkedEventIntoView({
    isDeepLinkedEvent: isDeepLinkedHiddenEvent,
    elementRef: elementRef,
  });

  const ariaLabel = t('Show {{count}} more overlapping events', { count: hiddenEventIds.length });
  return (
    <motion.button
      ref={elementRef}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0, zIndex: EVENT_Z_INDICES.appointment }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      css={[overlappingEventsButtonStyle, isOverflowingEventSelected && selectedEventStyle]}
      style={style}
      aria-label={ariaLabel}
      onClick={() => {
        onEventsSelect?.(hiddenEventIds);
      }}
      onMouseDown={(e) => e.stopPropagation()} // Prevents the button from triggering drag event creation when clicked
    >
      +{hiddenEventIds.length}
    </motion.button>
  );
};

const overlappingEventsButtonStyle = css({
  border: 'none',
  cursor: 'pointer',
  fontWeight: theme.font.weight.semibold,
  fontSize: theme.font.size.medium,
  justifySelf: 'stretch',
  alignSelf: 'start',
  width: OVERLAP_EVENTS_BUTTON_WIDTH,
  height: 40,
  borderRadius: theme.borderRadius.full,
  boxShadow: theme.shadows.light,
  background: theme.colors.white,
  color: theme.colors.neutral90,
  zIndex: EVENT_Z_INDICES.appointment,
  transition: 'box-shadow 0.2s ease-in-out, scale 0.2s ease-in-out, outline 0.1s linear',
  ':hover': {
    scale: 1.02,
    boxShadow: theme.shadows.floating,
  },
});
